import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Image as ImageIcon, ScanSearch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { API } from "@/App";
import AdminAddBusinessDialog from "@/components/AdminAddBusinessDialog";
import AdminBatchVerifyByKeywordsDialog from "@/components/AdminBatchVerifyByKeywordsDialog";
import {
  STATUS_TABS,
  StatusTabsList,
  AlphabetFilter,
  SourceFilterBar,
  SelectAllToggle,
} from "@/components/VerificationFilters";
import VerificationListItem from "@/components/VerificationListItem";

export default function AdminVerificationsTab() {
  const [status, setStatus] = useState("pending");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [letter, setLetter] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pings, setPings] = useState({});
  const [pingRunning, setPingRunning] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const rowRefs = useRef({});
  const [backfilling, setBackfilling] = useState(false);

  const fetch = useCallback(async (s = status, src = sourceFilter, ltr = letter) => {
    setLoading(true);
    setSelected(new Set());
    try {
      const params = { status: s, source: src, limit: 200 };
      if (ltr) params.letter = ltr;
      const res = await axios.get(`${API}/admin/businesses`, { params, withCredentials: true });
      setBusinesses(res.data?.businesses || []);
      setTotal(res.data?.total ?? 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load businesses");
      setBusinesses([]);
    } finally {
      setLoading(false);
    }
  }, [status, sourceFilter, letter]);

  useEffect(() => { fetch(status, sourceFilter, letter); }, [fetch, status, sourceFilter, letter]);

  // Ping websites for all currently visible businesses (background sweep).
  useEffect(() => {
    setPings({});
    setFocusIdx(0);
    if (!businesses.length) return;
    const ids = businesses.map((b) => b.business_id);
    let cancelled = false;
    (async () => {
      setPingRunning(true);
      try {
        const res = await axios.post(
          `${API}/admin/businesses/ping-websites`,
          { business_ids: ids },
          { withCredentials: true, timeout: 45000 },
        );
        if (!cancelled) setPings(res.data?.results || {});
      } catch {
        // silent
      } finally {
        if (!cancelled) setPingRunning(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businesses]);

  const setVerification = useCallback(async (business, next) => {
    setBusyId(business.business_id);
    try {
      await axios.patch(
        `${API}/admin/businesses/${business.business_id}/verification`,
        { is_verified: next },
        { withCredentials: true },
      );
      toast.success(`${business.name} ${next ? "verified" : "un-verified"}`);
      fetch(status, sourceFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setBusyId(null);
    }
  }, [fetch, status, sourceFilter]);

  // V/J/K hotkeys for verification workflow.
  useEffect(() => {
    if (status !== "pending") return;
    const pending = businesses.filter((b) => !b.is_verified);
    if (!pending.length) return;
    const scrollTo = (id) => rowRefs.current[id]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key === "v") {
        e.preventDefault();
        const target = pending[Math.min(focusIdx, pending.length - 1)];
        if (!target) return;
        const nextIdx = Math.min(focusIdx + 1, pending.length - 1);
        setFocusIdx(nextIdx);
        setVerification(target, true);
        if (pending[nextIdx]) scrollTo(pending[nextIdx].business_id);
      } else if (key === "j" || key === "arrowdown") {
        e.preventDefault();
        setFocusIdx((i) => {
          const n = Math.min(i + 1, pending.length - 1);
          if (pending[n]) scrollTo(pending[n].business_id);
          return n;
        });
      } else if (key === "k" || key === "arrowup") {
        e.preventDefault();
        setFocusIdx((i) => {
          const n = Math.max(i - 1, 0);
          if (pending[n]) scrollTo(pending[n].business_id);
          return n;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [status, businesses, focusIdx, setVerification]);

  const deleteBusiness = async (business) => {
    if (!window.confirm(`Delete "${business.name}" and all its products? This can't be undone.`)) return;
    setBusyId(business.business_id);
    try {
      await axios.delete(`${API}/admin/businesses/${business.business_id}`, { withCredentials: true });
      toast.success(`Deleted "${business.name}"`);
      fetch(status, sourceFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    } finally {
      setBusyId(null);
    }
  };

  const refreshLogo = async (business) => {
    setBusyId(business.business_id);
    try {
      await axios.post(
        `${API}/admin/logos/${business.business_id}/refresh`,
        {},
        { withCredentials: true, timeout: 60000 },
      );
      toast.success(`Logo refreshed for ${business.name}`);
      fetch(status, sourceFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not refresh logo");
    } finally {
      setBusyId(null);
    }
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelectedOnPage = businesses.length > 0 && businesses.every((b) => selected.has(b.business_id));
  const toggleSelectAll = () =>
    setSelected(allSelectedOnPage ? new Set() : new Set(businesses.map((b) => b.business_id)));

  const bulkVerify = async ({ useSelection, sourceLabel }) => {
    const payload = useSelection
      ? { business_ids: Array.from(selected), is_verified: true }
      : { source_filter: sourceFilter, is_verified: true };
    const target = useSelection
      ? `${selected.size} selected business${selected.size === 1 ? "" : "es"}`
      : sourceLabel;

    if (!useSelection && sourceFilter === "all" &&
        !window.confirm("Verify EVERY pending business (all sources)? This is a one-click blanket approval.")) return;
    if (useSelection && selected.size === 0) { toast.error("Nothing selected"); return; }
    if (!useSelection && !window.confirm(`Verify all pending ${sourceLabel}?`)) return;

    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/admin/businesses/bulk-verify`, payload, { withCredentials: true });
      const n = res.data?.modified_count ?? 0;
      toast.success(`Verified ${n} ${target}`);
      setSelected(new Set());
      fetch(status, sourceFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk verify failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const backfillLogos = async () => {
    if (backfilling) return;
    if (!window.confirm("Fetch logos for every business missing one? This calls each business's website.")) return;
    setBackfilling(true);
    try {
      const res = await axios.post(
        `${API}/admin/logos/backfill?only_missing=true&limit=200`,
        {},
        { withCredentials: true, timeout: 300000 },
      );
      const d = res.data || {};
      toast.success(`Fetched ${d.fetched} logo${d.fetched === 1 ? "" : "s"} (${d.failed} failed).`);
      fetch(status, sourceFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="admin-verifications-tab">
      <div className="p-6 border-b flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-xl text-tbc-navy">Business Directory Management</h2>
          <p className="text-sm text-gray-500 mt-1">
            Review and verify businesses, or add new listings directly to the directory.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setBatchOpen(true)}
            data-testid="batch-verify-open-btn"
            className="border-tbc-gold/60 text-tbc-navy hover:bg-tbc-gold/10"
            title="Batch-verify businesses whose descriptions mention 'black owned', 'african', 'ados', etc."
          >
            <ScanSearch className="w-4 h-4 mr-2" />
            Batch verify by keyword
          </Button>
          <Button type="button" variant="outline" onClick={backfillLogos} disabled={backfilling} data-testid="backfill-logos-btn">
            {backfilling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />}
            {backfilling ? "Fetching logos…" : "Fetch missing logos"}
          </Button>
          <AdminAddBusinessDialog onCreated={() => fetch(status, sourceFilter)} />
        </div>
      </div>

      <div className="p-6">
        <Tabs value={status} onValueChange={setStatus}>
          <StatusTabsList />
          <AlphabetFilter letter={letter} onChange={setLetter} />
          <SourceFilterBar
            sourceFilter={sourceFilter}
            onSourceChange={setSourceFilter}
            status={status}
            businesses={businesses}
            selected={selected}
            bulkBusy={bulkBusy}
            onVerifySelected={() => bulkVerify({ useSelection: true })}
            onVerifyAllOfSource={(sourceLabel) => bulkVerify({ useSelection: false, sourceLabel })}
          />

          {STATUS_TABS.map((t) => (
            <TabsContent key={t.value} value={t.value} className="m-0">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-xs text-gray-500" data-testid="admin-verifications-count">
                  {total} business{total === 1 ? "" : "es"} in this view
                  {pingRunning && <span className="ml-2 text-gray-400">· pinging websites…</span>}
                </p>
                <div className="flex items-center gap-3">
                  {t.value === "pending" && businesses.length > 0 && (
                    <>
                      <span className="hidden md:inline-flex text-[11px] text-gray-500 items-center gap-1" data-testid="hotkey-hint">
                        Hotkeys: press
                        <kbd className="mx-0.5 px-1.5 py-0.5 rounded border border-gray-300 bg-gray-50 font-mono">V</kbd>
                        to verify · <kbd className="mx-0.5 px-1.5 py-0.5 rounded border border-gray-300 bg-gray-50 font-mono">J</kbd>
                        /<kbd className="mx-0.5 px-1.5 py-0.5 rounded border border-gray-300 bg-gray-50 font-mono">K</kbd> to move
                      </span>
                      <SelectAllToggle allSelectedOnPage={allSelectedOnPage} onToggle={toggleSelectAll} />
                    </>
                  )}
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              ) : businesses.length === 0 ? (
                <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg" data-testid="admin-verifications-empty">
                  {t.value === "pending"
                    ? "No businesses pending verification — you're all caught up!"
                    : t.value === "verified"
                    ? "No verified businesses yet."
                    : "No businesses found."}
                </div>
              ) : (
                (() => {
                  const pendingList = businesses.filter((x) => !x.is_verified);
                  const pendingIndex = new Map(pendingList.map((b, i) => [b.business_id, i]));
                  return (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {businesses.map((b) => (
                        <VerificationListItem
                          key={b.business_id}
                          business={b}
                          status={status}
                          selected={selected.has(b.business_id)}
                          onToggleSelect={toggleSelected}
                          isFocused={t.value === "pending" && pendingIndex.get(b.business_id) === focusIdx}
                          rowRef={(el) => (rowRefs.current[b.business_id] = el)}
                          ping={pings[b.business_id]}
                          busy={busyId === b.business_id}
                          onVerify={(biz) => setVerification(biz, true)}
                          onUnverify={(biz) => setVerification(biz, false)}
                          onDelete={deleteBusiness}
                          onRefreshLogo={refreshLogo}
                          onLogoUploaded={() => fetch()}
                        />
                      ))}
                    </div>
                  );
                })()
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <AdminBatchVerifyByKeywordsDialog
        open={batchOpen}
        onOpenChange={setBatchOpen}
        onVerified={() => fetch(status, sourceFilter, letter)}
      />
    </div>
  );
}
